import { HttpBackend, HttpClient, HttpEvent, HttpEventType, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  catchError,
  concatMap,
  filter,
  finalize,
  from,
  map,
  Observable,
  of,
  switchMap,
  tap,
  throwError,
  toArray,
} from 'rxjs';

import { DocumentFile } from '@shared/models';
import { blobFile, generateGuid, getFileExtension } from '@shared/utils';

import { BaseService } from '../base.service';
import { LoadersService } from '../loaders.service';

import { LogsService } from './logs.service';

@Injectable({
  providedIn: 'root',
})
export class DocumentFileService extends BaseService {
  private readonly storageHttp: HttpClient;

  constructor(
    private http: HttpClient,
    httpBackend: HttpBackend,
    private loadersService: LoadersService,
    private logsService: LogsService
  ) {
    super();
    // Presigned storage requests must not receive API authentication headers.
    this.storageHttp = new HttpClient(httpBackend);
  }

  upload(file: File, loader: string): Observable<DocumentFile> {
    this.loadersService.show(loader, 1);

    const extension = getFileExtension(file.name);
    const blobName = `${generateGuid()}${extension}`;

    const fileToUpload = new File([file], blobName, { type: file.type });

    return this.getUploadUrl(fileToUpload.name).pipe(
      switchMap((sasUrl) =>
        this.uploadToStorage(sasUrl, fileToUpload, loader).pipe(
          filter((event) => event.type === HttpEventType.Response)
        )
      ),
      switchMap(() => this.completeStorageUpload(file.name, fileToUpload.name, fileToUpload.type)),
      finalize(() => this.loadersService.hide(loader))
    );
  }

  uploadMany(files: File[], loader: string): Observable<DocumentFile[]> {
    if (!files.length) return of([]);

    this.loadersService.show(loader, 1);
    const total = files.length;

    return from(files).pipe(
      concatMap((file, index) =>
        this.upload(file, '').pipe(
          tap(() => this.loadersService.update(loader, Math.round(((index + 1) / total) * 100))),
          catchError((error: Error) => {
            const { message, stack: stackTrace } = error;

            this.logsService.saveWebExceptionLog(message, stackTrace ?? '').subscribe({ error: () => {} });

            return throwError(() => error);
          })
        )
      ),
      toArray(),
      finalize(() => this.loadersService.hide(loader))
    );
  }

  download(file: DocumentFile, loader: string): Observable<void> {
    return this.getFile(file, loader).pipe(map((blob) => blobFile(blob, file.fileName)));
  }

  getFile(file: DocumentFile, loader: string): Observable<Blob> {
    return this.storageHttp
      .get(file.url, {
        responseType: 'blob',
        reportProgress: true,
        observe: 'events',
      })
      .pipe(
        tap((event) => {
          if (event.type === HttpEventType.DownloadProgress && event.total) {
            this.loadersService.update(loader, Math.round((100 * event.loaded) / event.total));
          }
        }),
        filter((event): event is HttpResponse<Blob> => event.type === HttpEventType.Response),
        map((response) => response.body as Blob),
        finalize(() => this.loadersService.hide(loader))
      );
  }

  getImageUrls(imageId: string, loader: string): Observable<DocumentFile> {
    this.loadersService.show(loader);
    return this.http
      .get<DocumentFile>(`${this.baseUrl}/document-files/${imageId}/urls`)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  delete(loader: string, id: string): Observable<boolean> {
    this.loadersService.show(loader);
    return this.http.delete(`${this.baseUrl}/document-files/${id}`, { observe: 'response' }).pipe(
      map((response) => this.isStatusSucceed(response.status)),
      finalize(() => this.loadersService.hide(loader))
    );
  }
  private getUploadUrl(fileName: string): Observable<string> {
    return this.http.get<string>(`${this.baseUrl}/document-files/get-upload-url`, {
      params: { fileName: fileName },
    });
  }

  private uploadToStorage(sasUrl: string, file: File, loader: string): Observable<HttpEvent<unknown>> {
    let headers = new HttpHeaders({ 'Content-Type': file.type });

    if (!this.isMinioPresignedUrl(sasUrl)) {
      headers = headers.set('x-ms-blob-type', 'BlockBlob');
    }

    return this.storageHttp
      .put(sasUrl, file, {
        headers,
        reportProgress: true,
        observe: 'events',
      })
      .pipe(
        tap((event) => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            const progress = Math.round((event.loaded / event.total) * 100);
            this.loadersService.update(loader, progress);
          }
        })
      );
  }

  private isMinioPresignedUrl(url: string): boolean {
    return new URL(url).searchParams.has('X-Amz-Algorithm');
  }

  private completeStorageUpload(
    originalFileName: string,
    fileName: string,
    contentType: string
  ): Observable<DocumentFile> {
    const formData = new FormData();
    formData.append('originalFileName', originalFileName);
    formData.append('fileName', fileName);
    formData.append('contentType', contentType);
    return this.http.post<DocumentFile>(`${this.baseUrl}/document-files/complete-upload`, formData);
  }
}
